import React, { useMemo } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { ViewUpdate, EditorView, Decoration, DecorationSet, MatchDecorator, ViewPlugin } from "@codemirror/view";
import { autocompletion, CompletionContext, CompletionResult } from "@codemirror/autocomplete";

// Token definition used for autocomplete and reference
export type TokenDef = {
    token: string;   // e.g. "<BaseGrantNumber>"
    scope: string;
    description: string;
};

type Props = {
    value: string;
    onChange: (value: string) => void;
    readOnly?: boolean;
    tokens: TokenDef[];
    height?: string;
    placeholder?: string;
};

// Match <TokenName> patterns — letters only inside angle brackets
const tokenPattern = /<[A-Z][A-Za-z]*>/g;

// Decoration for matched tokens
const tokenMark = Decoration.mark({ class: "cm-token-highlight" });

const tokenDecorator = new MatchDecorator({
    regexp: tokenPattern,
    decoration: () => tokenMark,
});

const tokenHighlightPlugin = ViewPlugin.fromClass(
    class {
        decorations: DecorationSet;
        constructor(view: EditorView) {
            this.decorations = tokenDecorator.createDeco(view);
        }
        update(update: ViewUpdate) {
            this.decorations = tokenDecorator.updateDeco(update, this.decorations);
        }
    },
    { decorations: (v) => v.decorations }
);

// Base theme for the editor
const editorTheme = EditorView.theme({
    "&": {
        fontSize: "13px",
        border: "1px solid #ddd",
        borderRadius: "4px",
    },
    "&.cm-focused": {
        outline: "none",
        borderColor: "#337ab7",
    },
    ".cm-scroller": {
        overflow: "auto",
    },
    ".cm-content": {
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        padding: "8px",
    },
    ".cm-line": {
        padding: "0 4px",
    },
    ".cm-token-highlight": {
        backgroundColor: "#e8f0fe",
        color: "#1a56db",
        borderRadius: "3px",
        padding: "1px 2px",
        fontWeight: "500",
    },
    ".cm-tooltip-autocomplete": {
        border: "1px solid #ddd",
        borderRadius: "4px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    },
    ".cm-tooltip-autocomplete ul li": {
        padding: "4px 8px",
    },
    ".cm-tooltip-autocomplete ul li[aria-selected]": {
        backgroundColor: "#337ab7",
        color: "white",
    },
    ".cm-completionLabel": {
        fontFamily: "monospace",
        fontSize: "12px",
    },
    ".cm-completionDetail": {
        fontStyle: "normal",
        fontSize: "11px",
        color: "#888",
        marginLeft: "8px",
    },
});

// Prevent wheel events from bubbling to parent scroll containers (e.g. DevExtreme Popup)
const preventWheelBubble = EditorView.domEventHandlers({
    wheel(event, view) {
        const scroller = view.scrollDOM;
        const { scrollTop, scrollHeight, clientHeight } = scroller;
        const atTop = scrollTop === 0 && event.deltaY < 0;
        const atBottom = scrollTop + clientHeight >= scrollHeight && event.deltaY > 0;
        if (!atTop && !atBottom) {
            event.stopPropagation();
        }
    },
});

// Build autocomplete source from token definitions
function buildTokenCompletion(tokens: TokenDef[]) {
    return (context: CompletionContext): CompletionResult | null => {
        // Trigger on < character — look back for an opening <
        const before = context.matchBefore(/<[A-Za-z]*/);
        if (!before) return null;

        return {
            from: before.from,
            options: tokens.map((t) => ({
                label: t.token,
                detail: t.description,
                type: "variable",
            })),
            filter: true,
        };
    };
}

export default function TokenEditor({ value, onChange, readOnly, tokens, height, placeholder }: Props) {
    const extensions = useMemo(() => {
        const exts = [
            tokenHighlightPlugin,
            editorTheme,
            EditorView.lineWrapping,
            preventWheelBubble,
            autocompletion({
                override: [buildTokenCompletion(tokens)],
                activateOnTyping: true,
            }),
        ];
        return exts;
    }, [tokens]);

    return (
        <CodeMirror
            value={value}
            onChange={onChange}
            extensions={extensions}
            readOnly={readOnly}
            height={height ?? "300px"}
            placeholder={placeholder}
            basicSetup={{
                lineNumbers: false,
                foldGutter: false,
                highlightActiveLine: false,
                highlightActiveLineGutter: false,
                indentOnInput: false,
                bracketMatching: false,
                closeBrackets: false,
                autocompletion: false, // we configure our own
                searchKeymap: false,
            }}
        />
    );
}
